import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import EnhancedErrorBoundary from "./components/EnhancedErrorBoundary";
import AppVersionManager from "./components/AppVersionManager";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import MonitoringPage from "./pages/MonitoringPage";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import TasksPage from "./pages/TasksPage";
import TaskDetailPage from "./pages/TaskDetailPage";
import OwnersPage from "./pages/OwnersPage";
import UsersPage from "./pages/UsersPage";
import ImportPage from "./pages/ImportPage";
import EmailAdminPage from "./pages/EmailAdminPage";
import CleaningCompaniesPage from "./pages/CleaningCompaniesPage";
import ContractorsPage from "./pages/ContractorsPage";
import TagsPage from "./pages/TagsPage";
import BackupPage from "./pages/BackupPage";
import SettingsPage from "./pages/SettingsPage";
import GuestPage from "./pages/GuestPage";
import ScanPage from "./pages/ScanPage";
import DeparturePage from "./pages/DeparturePage";
import PrintA4Page from "./pages/PrintA4Page";
import Layout from "./components/Layout";
import ForcedPasswordChange from "./components/ForcedPasswordChange";
import "./App.css";

/**
 * ProtectedRoute - Geschützte Route mit Auth-Check
 * Verwendet doppelten requestAnimationFrame für maximale DOM-Stabilität
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading, forcePasswordChange } = useAuth();
  const [isMounted, setIsMounted] = useState(false);
  const mountedRef = useRef(false);
  
  useEffect(() => {
    mountedRef.current = true;
    // Doppelter RAF für DOM-Stabilität auf Mobile
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (mountedRef.current) {
          setIsMounted(true);
        }
      });
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  if (loading || !isMounted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (forcePasswordChange) {
    return <ForcedPasswordChange />;
  }
  
  return <Layout>{children}</Layout>;
};

/**
 * AdminRoute - Admin-geschützte Route
 */
const AdminRoute = ({ children }) => {
  const { user, loading, forcePasswordChange } = useAuth();
  const [isMounted, setIsMounted] = useState(false);
  const mountedRef = useRef(false);
  
  useEffect(() => {
    mountedRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (mountedRef.current) {
          setIsMounted(true);
        }
      });
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  if (loading || !isMounted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user || user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (forcePasswordChange) {
    return <ForcedPasswordChange />;
  }
  
  return <Layout>{children}</Layout>;
};

/**
 * Prüft ob die aktuelle URL eine öffentliche QR-Code-Seite ist
 */
function isPublicQRPage() {
  const path = window.location.pathname;
  return path.startsWith('/departure/') || 
         path.startsWith('/guest/') || 
         path.startsWith('/scan/') ||
         path.startsWith('/print/');
}

/**
 * PublicPageWrapper - Wrapper für öffentliche Seiten
 */
const PublicPageWrapper = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const mountedRef = useRef(false);
  
  useEffect(() => {
    mountedRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (mountedRef.current) {
          setIsReady(true);
        }
      });
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
      </div>
    );
  }
  
  return children;
};

/**
 * ServiceWorkerManager - Registriert den Service Worker verzögert
 */
const ServiceWorkerManager = () => {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    
    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none'
        });
        console.log('[App] SW registered');
        setTimeout(() => {
          registration.update().catch(() => {});
        }, 10000);
      } catch (error) {
        console.warn('[App] SW registration failed:', error);
      }
    };
    
    // SW erst nach 5 Sekunden registrieren
    const timer = setTimeout(registerSW, 5000);
    return () => clearTimeout(timer);
  }, []);
  
  return null;
};

/**
 * AppRoutes - Route-Definitionen mit Key für sauberes Re-Mounting
 */
const AppRoutes = () => {
  const [routesReady, setRoutesReady] = useState(false);
  const mountedRef = useRef(false);
  
  useEffect(() => {
    mountedRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (mountedRef.current) {
          setRoutesReady(true);
        }
      });
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  if (!routesReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/monitoring" element={<ProtectedRoute><MonitoringPage /></ProtectedRoute>} />
      <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
      <Route path="/properties/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/tasks/:id" element={<ProtectedRoute><TaskDetailPage /></ProtectedRoute>} />
      <Route path="/owners" element={<ProtectedRoute><OwnersPage /></ProtectedRoute>} />
      <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
      <Route path="/import" element={<AdminRoute><ImportPage /></AdminRoute>} />
      <Route path="/email-admin" element={<AdminRoute><EmailAdminPage /></AdminRoute>} />
      <Route path="/cleaning-companies" element={<AdminRoute><CleaningCompaniesPage /></AdminRoute>} />
      <Route path="/contractors" element={<AdminRoute><ContractorsPage /></AdminRoute>} />
      <Route path="/tags" element={<AdminRoute><TagsPage /></AdminRoute>} />
      <Route path="/backup" element={<AdminRoute><BackupPage /></AdminRoute>} />
      <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

/**
 * Haupt-App Komponente
 * 
 * WICHTIG für insertBefore-Fix:
 * 1. Toaster ist IMMER gerendert (nicht conditional)
 * 2. Toaster ist am App-Root (außerhalb von Routen)
 * 3. React.StrictMode wurde entfernt (in index.js)
 * 4. Alle Routen haben isMounted-Pattern
 */
function App() {
  // Öffentliche Seiten: KOMPLETT ISOLIERT - KEIN Toaster
  if (isPublicQRPage()) {
    const path = window.location.pathname;
    
    return (
      <PublicPageWrapper>
        {path.startsWith('/departure/') && <DeparturePage />}
        {path.startsWith('/scan/') && <ScanPage />}
        {path.startsWith('/guest/') && <GuestPage />}
        {path.startsWith('/print/a4/') && <PrintA4Page />}
      </PublicPageWrapper>
    );
  }
  
  // Geschützte App mit Toaster am Root
  return (
    <BrowserRouter>
      <AuthProvider>
        <EnhancedErrorBoundary>
          <AppVersionManager />
          <ServiceWorkerManager />
          <AppRoutes />
          {/* 
            KRITISCH: Toaster MUSS am App-Root sein und IMMER gerendert werden!
            Kein conditional rendering, keine Verzögerung.
            Das ist die einzige stabile Konfiguration für React 19 + Sonner.
          */}
          <Toaster 
            position="bottom-right" 
            richColors 
            expand={false}
            closeButton={false}
          />
        </EnhancedErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
